// Sandboxed ad/script execution helpers.
//
// SECURITY: any embedded ad/widget <script> is executed inside a sandboxed
// iframe (sandbox="allow-scripts allow-same-origin allow-popups", NO
// allow-top-navigation) so it can render/click-through normally but can
// NEVER force a top-level page redirect - this is what stops a malicious ad
// creative from hijacking the whole page to a scam/virus-warning site.
//
// Consecutive scripts that rely on a shared JS global (an ad network's
// `atOptions` read by a following `invoke.js`, a widget's `$bp(...)` call
// after its library loads) are grouped into ONE shared iframe so they still
// work together, instead of isolating each into its own realm.

const isEmptyAdDiv = (el) => el && el.tagName === 'DIV' && el.id && el.innerHTML.trim() === '';
const isScript = (el) => el && el.tagName === 'SCRIPT';

// Mounts one ordered group of scripts inside a single sandboxed iframe.
// Returns a cleanup function.
export const runScriptsInSandbox = (container, scripts, nonScriptHtml = '') => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
  iframe.setAttribute('scrolling', 'no');
  iframe.setAttribute('title', 'embedded-ad');
  iframe.style.border = 'none';
  iframe.style.width = '100%';
  iframe.style.minHeight = '1px';
  iframe.style.display = 'block';
  container.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) return () => {};

  doc.open();
  doc.write('<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;background:transparent;overflow:hidden;}</style></head><body></body></html>');
  doc.close();

  if (nonScriptHtml) doc.body.insertAdjacentHTML('beforeend', nonScriptHtml);

  let resizeObserver;
  try {
    const RO = iframe.contentWindow.ResizeObserver || window.ResizeObserver;
    resizeObserver = new RO(() => {
      const h = doc.body.scrollHeight;
      if (h > 0) iframe.style.height = `${h}px`;
    });
    resizeObserver.observe(doc.body);
  } catch (e) {}

  const runNext = (i) => {
    if (i >= scripts.length) return;
    const info = scripts[i];
    const script = doc.createElement('script');
    (info.attrs || []).forEach((attr) => {
      if (attr.name !== 'src') script.setAttribute(attr.name, attr.value);
    });
    if (info.src) {
      script.addEventListener('load', () => runNext(i + 1));
      script.addEventListener('error', () => runNext(i + 1));
      script.src = info.src;
      doc.body.appendChild(script);
    } else {
      script.textContent = info.innerHTML || '';
      doc.body.appendChild(script);
      runNext(i + 1);
    }
  };
  runNext(0);

  return () => {
    try { resizeObserver && resizeObserver.disconnect(); } catch (e) {}
  };
};

// Parses a raw HTML string, groups consecutive <script> tags (+ adjacent
// empty ad-container <div id="...">) into shared sandboxed iframes, and
// renders everything else directly into `container` (fully visible/SEO-safe).
// Returns a cleanup function.
export const mountSandboxedHtml = (container, html) => {
  container.innerHTML = '';
  if (!html) return () => {};

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
  const blankTextNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.trim() === '') blankTextNodes.push(node);
  }
  blankTextNodes.forEach((n) => n.remove());

  const adUnits = [];
  const visited = new Set();

  Array.from(tempDiv.querySelectorAll('script')).forEach((scriptNode) => {
    if (visited.has(scriptNode)) return;

    const groupNodes = [scriptNode];
    visited.add(scriptNode);

    let cursor = scriptNode;
    while (isScript(cursor.nextElementSibling) && !visited.has(cursor.nextElementSibling)) {
      cursor = cursor.nextElementSibling;
      groupNodes.push(cursor);
      visited.add(cursor);
    }

    const leadingAdDiv = isEmptyAdDiv(groupNodes[0].previousElementSibling)
      ? groupNodes[0].previousElementSibling
      : null;
    const trailingAdDiv = isEmptyAdDiv(cursor.nextElementSibling)
      ? cursor.nextElementSibling
      : null;

    const nonScriptParts = [];
    if (leadingAdDiv) nonScriptParts.push(leadingAdDiv.outerHTML);
    if (trailingAdDiv) nonScriptParts.push(trailingAdDiv.outerHTML);

    const scriptInfos = groupNodes.map((s) => ({
      src: s.getAttribute('src'),
      innerHTML: s.innerHTML,
      attrs: Array.from(s.attributes),
    }));

    const placeholder = document.createElement('span');
    placeholder.setAttribute('data-ad-unit-index', String(adUnits.length));
    placeholder.style.display = 'block';
    groupNodes[0].replaceWith(placeholder);
    groupNodes.slice(1).forEach((s) => s.remove());
    if (leadingAdDiv) leadingAdDiv.remove();
    if (trailingAdDiv) trailingAdDiv.remove();

    adUnits.push({ nonScriptHtml: nonScriptParts.join(''), scripts: scriptInfos });
  });

  container.innerHTML = tempDiv.innerHTML;

  const cleanups = [];
  adUnits.forEach((unit, idx) => {
    const placeholder = container.querySelector(`[data-ad-unit-index="${idx}"]`);
    if (!placeholder) return;
    const iframeSlot = document.createElement('span');
    iframeSlot.style.display = 'block';
    placeholder.replaceWith(iframeSlot);
    cleanups.push(runScriptsInSandbox(iframeSlot, unit.scripts, unit.nonScriptHtml));
  });

  return () => cleanups.forEach((fn) => fn());
};
